import {
  type IAdminWithdrawalOrderCandidate,
  type IOrderWarehouseOrderSummary,
  type IWarehouseOrderDetail,
  type IWarehouseOrderEventList,
  type IWarehouseOrderList,
  type IWarehouseOrderReport,
  type IWarehouseOrderReportFilters,
  type IWarehouseOrderUnpairedProduct,
  type IWarehouseOrderUnpairedProductFilters,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import {
  type IWarehouseOrderEventsQuery,
  type IWarehouseOrdersQuery,
  type IWarehouseOrdersSyncInput,
  type IWarehouseOrderState,
  type IWarehouseOrderUnpairedProductPairInput,
} from './types';

export const preloadedState: IWarehouseOrderState = {
  ...asyncStateInitial,
  list: undefined,
  detail: undefined,
  events: undefined,
  report: undefined,
  byOrder: undefined,
  orderSearchResults: undefined,
  unpairedProducts: undefined,
  isListStale: false,
};

const warehouseOrderSlice = createSlice({
  name: 'WarehouseOrder',
  initialState: preloadedState,
  reducers: {
    warehouseOrdersLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(query: IWarehouseOrdersQuery) {
        return { payload: query };
      },
    },
    warehouseOrdersLoadResponse: {
      reducer(state, action: { payload: { list: IWarehouseOrderList } }) {
        state.list = action.payload.list;
        state.isListStale = false;
        finishLoading(state);
      },
      prepare(list: IWarehouseOrderList) {
        return { payload: { list } };
      },
    },
    warehouseOrdersRecalculate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(query: IWarehouseOrdersQuery) {
        return { payload: query };
      },
    },
    warehouseOrderLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    warehouseOrderLoadResponse: {
      reducer(
        state,
        action: { payload: { detail: IWarehouseOrderDetail | null } },
      ) {
        state.detail = action.payload.detail;
        finishLoading(state);
      },
      prepare(detail: IWarehouseOrderDetail | null) {
        return { payload: { detail } };
      },
    },
    warehouseOrderPair: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string, orderUuid: string) {
        return { payload: { uuid, orderUuid } };
      },
    },
    warehouseOrderUnpair: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string, orderUuid: string) {
        return { payload: { uuid, orderUuid } };
      },
    },
    warehouseOrderRecalculate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    warehouseOrderOrderSearch: {
      reducer(state) {
        state.orderSearchResults = undefined;
      },
      prepare(query: string) {
        return { payload: { query } };
      },
    },
    warehouseOrderOrderSearchResponse: {
      reducer(
        state,
        action: { payload: { orders: IAdminWithdrawalOrderCandidate[] } },
      ) {
        state.orderSearchResults = action.payload.orders;
      },
      prepare(orders: IAdminWithdrawalOrderCandidate[]) {
        return { payload: { orders } };
      },
    },
    warehouseOrderEventsLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(query: IWarehouseOrderEventsQuery) {
        return { payload: query };
      },
    },
    warehouseOrderEventsLoadResponse: {
      reducer(
        state,
        action: { payload: { events: IWarehouseOrderEventList } },
      ) {
        state.events = action.payload.events;
        finishLoading(state);
      },
      prepare(events: IWarehouseOrderEventList) {
        return { payload: { events } };
      },
    },
    warehouseOrderReportLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(filters: IWarehouseOrderReportFilters) {
        return { payload: filters };
      },
    },
    warehouseOrderReportLoadResponse: {
      reducer(state, action: { payload: { report: IWarehouseOrderReport } }) {
        state.report = action.payload.report;
        finishLoading(state);
      },
      prepare(report: IWarehouseOrderReport) {
        return { payload: { report } };
      },
    },
    warehouseOrdersSync: {
      reducer(state) {
        startLoading(state);
      },
      prepare(input: IWarehouseOrdersSyncInput) {
        return { payload: input };
      },
    },
    warehouseOrdersSyncResponse: {
      reducer(state) {
        finishLoading(state);
      },
      prepare(requested: number) {
        return { payload: { requested } };
      },
    },
    warehouseOrdersLoadByOrder: {
      reducer(state) {
        state.byOrder = undefined;
      },
      prepare(orderUuid: string) {
        return { payload: { orderUuid } };
      },
    },
    warehouseOrdersLoadByOrderResponse: {
      reducer(
        state,
        action: { payload: { summary: IOrderWarehouseOrderSummary } },
      ) {
        state.byOrder = action.payload.summary;
      },
      prepare(summary: IOrderWarehouseOrderSummary) {
        return { payload: { summary } };
      },
    },
    warehouseOrderUnpairedProductsLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(filters: IWarehouseOrderUnpairedProductFilters) {
        return { payload: filters };
      },
    },
    warehouseOrderUnpairedProductsLoadResponse: {
      reducer(
        state,
        action: {
          payload: { unpairedProducts: IWarehouseOrderUnpairedProduct[] };
        },
      ) {
        state.unpairedProducts = action.payload.unpairedProducts;
        finishLoading(state);
      },
      prepare(unpairedProducts: IWarehouseOrderUnpairedProduct[]) {
        return { payload: { unpairedProducts } };
      },
    },
    warehouseOrderUnpairedProductPair: {
      reducer(state) {
        startLoading(state);
      },
      prepare(input: IWarehouseOrderUnpairedProductPairInput) {
        return { payload: input };
      },
    },
    warehouseOrdersMarkStale(state) {
      state.isListStale = true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('WarehouseOrder'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  warehouseOrderEventsLoad,
  warehouseOrderEventsLoadResponse,
  warehouseOrderLoad,
  warehouseOrderLoadResponse,
  warehouseOrderOrderSearch,
  warehouseOrderOrderSearchResponse,
  warehouseOrderPair,
  warehouseOrderRecalculate,
  warehouseOrderReportLoad,
  warehouseOrderReportLoadResponse,
  warehouseOrdersLoad,
  warehouseOrdersLoadByOrder,
  warehouseOrdersLoadByOrderResponse,
  warehouseOrdersLoadResponse,
  warehouseOrdersMarkStale,
  warehouseOrdersRecalculate,
  warehouseOrdersSync,
  warehouseOrdersSyncResponse,
  warehouseOrderUnpair,
  warehouseOrderUnpairedProductPair,
  warehouseOrderUnpairedProductsLoad,
  warehouseOrderUnpairedProductsLoadResponse,
} = warehouseOrderSlice.actions;
export default warehouseOrderSlice.reducer;
